import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "@mui/material/Avatar";
import { toast } from "react-toastify";
import { ReactComponent as Close } from "../../../../assets/images/teamclose.svg";
import SearchInput from "../../../common/SearchInputField";
import { CircularProgress, Box, Skeleton, Typography } from "@mui/material";
import { chracterPosition } from "../../../../utils/index";
import { colorCodesForAvatar } from "../../../../constants/index";
import NoSearch from "../../../common/NoSearch";
import { ListCoRoleName } from "../../../../store/features/ListCoUserByRole";
import { assignFeedback } from "../../../../store/features/assignFeedbackQueue";
import { getKbFeedback } from "../../../../store/features/getKbFeedbackQueue";

// Skeleton component for agent items
const AgentItemSkeleton = () => (
  <div style={{ padding: '8px 0px' }} className="ChatPopup-Darleen-Container">
    <Skeleton variant="circular" width={40} height={40} />
    <div style={{ marginLeft: '12px', flex: 1 }}>
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="40%" height={16} />
    </div>
  </div>
);

function AssignFeedbackQueue(props) {
  const { isOpen, onClose, selectedItem, onAssignSuccess, groupId } = props;
  const dispatch = useDispatch();
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const [assignedList, setAssignedList] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Set assigned agent from selectedItem (now has fullData)
  useEffect(() => {
    if (selectedItem?.fullData?.assigneeId) {
      const assigneeData = selectedItem.fullData.assigneeId;

      setAssignedList({
        full_name: assigneeData.full_name || assigneeData.fullName || assigneeData.name || 'Unknown',
        agent_image: assigneeData.agent_image || assigneeData.agentImage || assigneeData.image || assigneeData.avatar || null,
        _id: assigneeData._id || assigneeData.id
      });
    } else {
      setAssignedList(null);
    }
  }, [selectedItem]);

  // Fetch agents when modal opens
  useEffect(() => {
    const fetchAgents = async () => {
      if (!isOpen) return;

      setIsLoading(true);
      try {
        const response = await dispatch(ListCoRoleName({
          name: 'agent,admin,admin_with_billing',
          group_id: groupId,
        }));
        if (response.payload?.status === 200) {
          const assigneeData = selectedItem?.fullData?.assigneeId;
          const currentAssigneeId = assigneeData?._id || assigneeData?.id;
          const agentsList = response.payload.data || [];

          // Feedback assignee payload often omits profile image — enrich from agents list
          if (currentAssigneeId) {
            const matchedAssignee = agentsList.find(
              (item) => item._id === currentAssigneeId || item.id === currentAssigneeId
            );
            if (matchedAssignee) {
              setAssignedList({
                full_name:
                  matchedAssignee.full_name ||
                  matchedAssignee.fullName ||
                  matchedAssignee.name ||
                  assigneeData?.full_name ||
                  'Unknown',
                agent_image:
                  matchedAssignee.agent_image ||
                  matchedAssignee.agentImage ||
                  matchedAssignee.image ||
                  matchedAssignee.avatar ||
                  null,
                _id: matchedAssignee._id || matchedAssignee.id,
              });
            }
          }

          const filteredAgents = agentsList
            .filter(
              (item) =>
                String(item.kbRole?.role || "").toLowerCase() !== "viewer"
            )
            .filter(item => (!currentAssigneeId || (item._id !== currentAssigneeId && item.id !== currentAssigneeId)))
            .map(item => ({
              id: item._id,
              img: item.agent_image || item.agentImage || item.image || item.avatar,
              name: item.full_name || item.fullName || item.name,
              email: item.email
            }));

          setData(filteredAgents);
          setFilteredData(filteredAgents);
        } else {
          toast.error('Failed to fetch agents');
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
        toast.error('Error fetching agents');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgents();
  }, [isOpen, dispatch, selectedItem, groupId]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAgent(null);
      setSearchQuery("");
    }
  }, [isOpen]);

  const handleAgentClick = (id) => {
    setSelectedAgent(id);
  };

  const handleAssignAgent = async () => {
    if (!selectedAgent) return;

    setAssignLoading(true);
    try {
      const assignResponse = await dispatch(assignFeedback({
        feedbackId: selectedItem.id || selectedItem._id,
        assigneeId: selectedAgent,
        business_id: businessId,
        group_id: groupId,
      }));

      if (assignResponse.payload?.status !== 200) {
        throw new Error(assignResponse.payload?.message || 'Assign failed');
      }

      const selectedAgentData = data.find(agent => agent.id === selectedAgent);
      toast.success("Feedback Assigned Successfully.");

      dispatch(getKbFeedback({
        business_id: businessId,
        group_id: groupId,
        page: 1,
        limit: 10,
        search: ""
      }));

      // Build updated item with correct assignee field
      const updatedItem = {
        ...selectedItem,
        assignee: selectedAgentData?.name || 'Assigned',
        status: selectedItem.status || 'In Progress',
        fullData: {
          ...selectedItem.fullData,
          assigneeId: {
            _id: selectedAgent,
            full_name: selectedAgentData?.name,
            email: selectedAgentData?.email,
            agent_image: selectedAgentData?.img
          }
        }
      };

      // Notify parent (will update global state)
      if (onAssignSuccess) {
        onAssignSuccess(updatedItem);
      }

      onClose();
    } catch (error) {
      console.error('Error assigning feedback:', error);
      toast.error(error.message || 'Failed to assign feedback');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filteredResults = data.filter((row) =>
      row.name.toLowerCase().includes(query) || (row.email && row.email.toLowerCase().includes(query))
    );
    setFilteredData(filteredResults);
  };

  const isTransferDisabled = selectedAgent == null;

  if (!isOpen) return null;

  const hasAgents = filteredData.length > 0 || !searchQuery;
  const showNoResults = searchQuery && filteredData.length === 0;

  return (
    <>
      <div className="overlay" style={{ zIndex: 1200 }} />
      <div
        style={{ top: '25%', zIndex: 1201, position: 'fixed' }}
        className="Assign-PopUp-Container"
      >
        <div style={{ marginBottom: '16px' }} className="Team-PopUp-Header">
          <p className="PopUp-Text01">Assign Feedback</p>
          <p className="PopUp-Text02" onClick={onClose}>
            <Close />
          </p>
        </div>

        {/* Search Input */}
        <SearchInput
          title=""
          placeholder="Search"
          className="SearchInputField-FeedBack"
          value={searchQuery}
          onChange={handleSearchInputChange}
        />

        {/* Content with skeleton loading */}
        {isLoading ? (
          <div className="AgentsList">
            {/* Show skeleton for assigned agent if exists */}
            {assignedList && (
              <>
                <AgentItemSkeleton />
                <hr
                  style={{
                    backgroundColor: "#F1F3F5",
                    height: "1px",
                    border: "none",
                    margin: "8px 0",
                  }}
                />
              </>
            )}
            {/* Show skeleton for available agents */}
            {[...Array(3)].map((_, index) => (
              <AgentItemSkeleton key={index} />
            ))}
          </div>
        ) : (
          <>
            {/* Show assigned agent if exists */}
            {assignedList && (
              <div style={{ marginTop: '19px' }} >
                <div style={{ padding: '0px' }} className="ChatPopup-Darleen-Container">
                  <p>
                    {assignedList?.agent_image ? (
                      <img
                        src={assignedList.agent_image}
                        alt="assigned user"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          objectFit: "cover"
                        }}
                        onError={(e) => {
                          // Hide broken image and show avatar fallback
                          e.target.style.display = 'none';
                          const avatar = e.target.parentElement.querySelector('.fallback-avatar');
                          if (avatar) avatar.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <Avatar
                        className="fallback-avatar"
                        sx={{
                          bgcolor:
                            colorCodesForAvatar[
                            chracterPosition(
                              assignedList.full_name
                                ?.substring(0, 1)
                                ?.toUpperCase()
                            )
                            ],
                          width: 40,
                          height: 40
                        }}
                      >
                        {assignedList.full_name?.substring(0, 1)?.toUpperCase()}
                      </Avatar>
                    )}
                    {assignedList?.agent_image && (
                      <Avatar
                        className="fallback-avatar"
                        sx={{
                          bgcolor:
                            colorCodesForAvatar[
                            chracterPosition(
                              assignedList.full_name
                                ?.substring(0, 1)
                                ?.toUpperCase()
                            )
                            ],
                          width: 40,
                          height: 40,
                          display: 'none'
                        }}
                      >
                        {assignedList.full_name?.substring(0, 1)?.toUpperCase()}
                      </Avatar>
                    )}
                  </p>
                  <div>
                    <p className="Albert-text">{assignedList.full_name}</p>
                    <p className="No-activetext">Assigned</p>
                  </div>
                </div>
                {hasAgents && (
                  <hr
                    style={{
                      backgroundColor: "#F1F3F5",
                      height: "1px",
                      border: "none",
                      margin: "8px 0",
                    }}
                  />
                )}
              </div>
            )}

            {/* Show available agents list or empty state */}
            <div style={{ maxHeight: '250px', overflowY: 'auto', marginTop: assignedList ? '0px' : '19px' }} >
              {showNoResults ? (
                <NoSearch
                  title="No Agent Found"
                  description="Clear your search and try again."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              ) : filteredData.length === 0 && !assignedList ? (
                <NoSearch
                  title="No Agents Available"
                  description="No agents to assign."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              ) : (
                filteredData.map((item) => (
                  <div
                    style={{
                      padding: selectedAgent === item.id ? '8px' : '8px 0px',
                      cursor: 'pointer'
                    }}
                    className={`ChatPopup-Darleen-Container ${selectedAgent === item.id ? "selected" : ""}`}
                    key={item.id}
                    onClick={() => handleAgentClick(item.id)}
                  >
                    <p>
                      {item.img ? (
                        <img
                          src={item.img}
                          alt="agent"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover"
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const avatar = e.target.parentElement.querySelector('.fallback-avatar');
                            if (avatar) avatar.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <Avatar
                          className="fallback-avatar"
                          sx={{
                            bgcolor:
                              colorCodesForAvatar[
                              chracterPosition(
                                item?.name?.substring(0, 1)?.toUpperCase()
                              )
                              ],
                            width: 40,
                            height: 40
                          }}
                        >
                          {item?.name?.substring(0, 1)?.toUpperCase()}
                        </Avatar>
                      )}
                      {item.img && (
                        <Avatar
                          className="fallback-avatar"
                          sx={{
                            bgcolor:
                              colorCodesForAvatar[
                              chracterPosition(
                                item?.name?.substring(0, 1)?.toUpperCase()
                              )
                              ],
                            width: 40,
                            height: 40,
                            display: 'none'
                          }}
                        >
                          {item?.name?.substring(0, 1)?.toUpperCase()}
                        </Avatar>
                      )}
                    </p>
                    <div>
                      <p className="Albert-text">{item?.name}</p>
                      <p className="No-activetext">{item?.email}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <div style={{ marginTop: '24px' }} className="AssignChat-footer">
          <p className="Add-PrivateChat">{/* Add Private Chat */}</p>
          <button
            style={{ height: '40px' }}
            className="AddTansfer"
            onClick={handleAssignAgent}
            disabled={isTransferDisabled || isLoading || assignLoading}
          >
            {assignLoading ? <CircularProgress size={20} color="inherit" /> : "Assign"}
          </button>
        </div>
      </div>
    </>
  );
}

export default AssignFeedbackQueue;